import { useCallback, useEffect, useRef, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useLocation, useParams } from "react-router-dom";
import DeleteModel, { confirmBulkDelete } from "../../../../Components/DeleteModel";
import {
 deleteItem,
 deleteMultipleItem,
 fetchData,
 submitForm,
 toggleStatus,
} from "../../../../utils/apiUtils";
import {
 ActionButton,
 DeleteButton,
 PlusButton,
 SwitchToggle,
 ToggleSwitch,
} from "../../../../helper/ImageFormat";
import CustomPagination from "../../../../helper/Pagination";
import debounce from "lodash.debounce";
import Spinner from "../../../../Components/spinner";
import Breadcrumb from "../../../../helper/Breadcrumb";
import PerPageSelect from "../../../../Components/PerPageSelect";
import TableSearch from "../../../../Components/TableSearch";
import dateformate from "../../../../helper/DateFormate";
import BackButton from "../../../../Components/BackButton";

const AttributeValue = () => {
 const selectAllRef = useRef(null);
 const param = useParams();
 const location = useLocation();
 const params = param.id;
 const [loading, setLoading] = useState(true);
 const [attValueData, setAttValueData] = useState([]);
 const [editingId, setEditingId] = useState(null);
 const attributeName = location?.state?.name;
 const attribute = location?.state;
 const [search, setSearch] = useState("");
 const [isDeleting, setIsDeleting] = useState(false);
 const [btnloading, setBtnLoading] = useState(false);
 const [selected, setSelected] = useState([]);
 const [errors, setErrors] = useState({
  name: "",
  colour: "",
 });
 const [data, setData] = useState({
  name: "",
  attribute_id: params,
  colour: attribute?.type === "Colour" && "",
  value: attribute?.type === "Label" && "",
  isActive:true
 });

 const [deleteItemId, setDeleteItemId] = useState({});
 const [totalCount, setTotalCount] = useState(0);
 const [currentPage, setCurrentPage] = useState(1);
 const [perPage, setPerPage] = useState(10);
 const [modalOpen, setModalOpen] = useState(false);
 const [deleteModalOpen, setDeleteModalOpen] = useState(false);

 const toggleModal = () => {
  setModalOpen(!modalOpen);
  if (modalOpen) {
   setEditingId(null);
   setData({
    name: "",
    colour: "",
    attribute_id: params,
    value: "",
   });
   setErrors({ name: "", colour: "" });
  }
 };

 const handleEditClick = (item) => {
  setEditingId(item?.id);

  setData({
   name: item?.name,
   colour: item?.colour,
   attribute_id: item?.attr_id,
   value: item?.value,
  isActive: item?.isActive ?? true,
  });
  setModalOpen(true);
 };

 const totalPages = Math.ceil(totalCount / perPage);

 const handleChange = (e) => {
  const { name, value } = e.target;
  setData({ ...data, [name]: value });
  setErrors({ ...errors, [name]: "" });
 };

 const handleAddAttValue = () => {
  setData({
   name: "",
   colour: "",
   attribute_id: params,
   value: "",
   isActive:true
  });
  setErrors({ name: "", colour: "" });
  setEditingId(null);
  setModalOpen(true);
 };

 const fetchAttributeValue = async (page = 1, searchQuery = "") => {
  const requestData = {
   attribute_id: params,
   perPage: perPage,
   pageNo: page,
   search: searchQuery,
  };
  await fetchData(
   "attribute-pagination-value",
   requestData,
   setAttValueData,
   setTotalCount,
   setCurrentPage,
   setLoading
  );
 };

 const debouncedFetchCustomers = useCallback(
  debounce(fetchAttributeValue, 1000),
  [perPage]
 );

 useEffect(() => {
  debouncedFetchCustomers(currentPage, search);
 }, [perPage, search]);

 const handlePageClick = (page) => {
  if (page >= 1 && page <= totalPages) {
   fetchAttributeValue(page);
  }
 };

 const handleSubmit = async (e) => {
  e.preventDefault();
  let hasError = false;

  if (!data.name) {
   setErrors((prev) => ({
    ...prev,
    name: `${attributeName} name is required`,
   }));
   hasError = true;
  }

  if (attribute?.type === "Colour" && !data.colour) {
   setErrors((prev) => ({
    ...prev,
    colour: "Color code is required",
   }));
   hasError = true;
  }

  if (hasError) return;

  setBtnLoading(true);

  const formData = {
   name: data.name,
   attribute_id: params,
   isActive:data.isActive,
   ...(data.colour && { colour: data.colour }),
   ...(data.value && { value: data.value }),
  };

  try {
   await submitForm({
    data: formData,
    url: "/attribute-value",
    editingId,
    validationErrorsSetter: setErrors,
    onSuccess: () => fetchAttributeValue(),
    closeModal: () => setModalOpen(false),
   });
  } catch (errors) {
   return errors;
  } finally {
   setBtnLoading(false);
  }
 };

 const handleDelete = async (data) => {
  setDeleteItemId(data);
  setDeleteModalOpen(true);
 };

 const confirmDelete = async () => {
  setIsDeleting(true);
  try {
   const res = await deleteItem(
    "attribute-value",
    deleteItemId?.id,
    fetchAttributeValue
   );
   setDeleteItemId({});
   setDeleteModalOpen(false);
   return res;
  } catch (error) {
   return error;
  } finally {
   setIsDeleting(false);
  }
 };



 const handlebulkDelete = async () => {
   await confirmBulkDelete({
    itemType: "Attribute value",
    selectedIds: selected,
    deleteApiCall: (ids) => deleteMultipleItem("attribute-value-bulk-delete", ids),
    refreshCallback: fetchAttributeValue,
   });
  };


 const handleStatusToggle = async (item) => {
  const data = await toggleStatus(
   "attribute-value-status",
   item.id,
   fetchAttributeValue
  );

  if (Object.keys(data).length > 0) {
   setAttValueData((prevattValueData) =>
    prevattValueData.map((curr) =>
     curr.id === item.id ? { ...curr, isActive: data?.isActive } : curr
    )
   );
  }
 };

 const isAllSelected =
  attValueData.length > 0 && selected.length === attValueData.length;
 const isIndeterminate =
  selected.length > 0 && selected.length < attValueData.length;

 useEffect(() => {
  if (selectAllRef.current) {
   selectAllRef.current.indeterminate = isIndeterminate;
  }
 }, [isIndeterminate]);

 const toggleSelectAll = () => {
  setSelected(isAllSelected ? [] : attValueData.map((b) => b.id));
 };

 const toggleSelectOne = (id) => {
  setSelected((prev) =>
   prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
  );
 };

 const Tableheader = ["Name","Updated At", "Is Active", "Action"];
 const breadcrumbdata = [
  { label: "Home", path: "/" },
  { label: "Attributes", path: "/attributes" },
  { label: "Attributes-Values", path: "/attribute-value" },
 ];

 return (
  <>
   <div className="content-wrapper">
    <div
     className="page-header mt-5"
     style={{ display: "flex", justifyContent: "space-between" }}
    >
     <h3 className="custom-page-title"> {attributeName} Attributes Value</h3>
     <Breadcrumb items={breadcrumbdata} />
    </div>

    <div
     className="d-flex justify-content-between mb-2"
     style={{ background: "#fff", padding: "10px", marginTop: "-20px" }}
    >
      <PerPageSelect value={perPage} onChange={setPerPage} />

     <div style={{ display: "flex", gap: "20px" }}>
      <BackButton />
     <TableSearch value={search} onChange={setSearch} />
      <PlusButton handleAdd={handleAddAttValue} module={"Attributes Value"} />
       {selected && selected?.length > 0 && (
             <DeleteButton
              handleAdd={handlebulkDelete}
              name={"selected Attribute value"}
              module={"Attributes"}
             />
            )}
     </div>
    </div>

    <div className="row">
     <div className="col-md-12 grid-margin stretch-card">
      <div className="card">
       <div className="card-body">
        <div className="pb-20 table-responsive ">
         <table
          className=" custom-table table table-striped"
          id="attributevalue"
         >
          <thead>
           <tr>
            <th>
             <input
              type="checkbox"
              ref={selectAllRef}
              checked={isAllSelected}
              onChange={toggleSelectAll}
             />
            </th>
            {Tableheader &&
             Tableheader.length > 0 &&
             Tableheader.map((item, index) => (
              <th className="font-weight-bold text-left" key={index}>
               {item}
              </th>
             ))}
           </tr>
          </thead>

          <tbody>
           {loading ? (
            <tr>
             <td colSpan="12" className="text-center">
              <Spinner />
             </td>
            </tr>
           ) : attValueData.length > 0 ? (
            attValueData?.map((attValue, id) => {
             return (
              <tr key={id}>
               <td>
                <input
                 type="checkbox"
                 checked={selected.includes(attValue.id)}
                 onChange={() => toggleSelectOne(attValue.id)}
                />
               </td>
              <td className="text-left">
  <div className="d-flex align-items-center">
    {attribute?.type === "Colour" && (
      <span
        className="color-circle"
        style={{ backgroundColor: attValue.colour }}
        title={attValue.colour}
      ></span>
    )}
    <span className="mx-3">{attValue.name}</span>
  </div>
</td>

               <td>{dateformate(attValue.updatedAt)}</td>
               {/* <td>{dateformate(attValue.createdAt)}</td> */}
               <td className="text-left ">
                <ToggleSwitch
                 data={attValue}
                 name="isActive"
                 checked={attValue.isActive}
                 handleStatusToggle={handleStatusToggle}
                 module={"Attributes Value"}
                />
               </td>
               <td className="text-left">
                <ActionButton
                 data={attValue}
                 handleEditClick={handleEditClick}
                 handleDelete={handleDelete}
                 module={"Attributes Value"}
                />
               </td>
              </tr>
             );
            })
           ) : (
            <tr>
             <td colSpan="12" className="text-center">
              <h4>No data available</h4>
             </td>
            </tr>
           )}
          </tbody>
         </table>
        </div>

        {attValueData.length > 0 && (
         <div>
          <CustomPagination
           currentPage={currentPage}
           totalPages={totalPages}
           onPageChange={handlePageClick}
           perPage={perPage}
           totalCount={totalCount}
          />
         </div>
        )}
       </div>
      </div>
     </div>
    </div>
   </div>

   <Modal
    isOpen={modalOpen}
    toggle={toggleModal}
    centered
    className="white-modal"
   >
    <ModalHeader toggle={toggleModal} className="close-currency">
     {editingId ? "Edit" : "Add"} {attributeName} value
    </ModalHeader>
    <form onSubmit={handleSubmit}>
     <ModalBody className="currency">
      <div className="mb-4">
       <input
        type="text"
        className="form-control form-control-sm border border-dark"
        id="name"
        name="name"
        placeholder={`Enter ${location?.state?.key} name`}
        value={data.name}
        onChange={handleChange}
       />
       {errors.name && <small className="text-danger">{errors.name}</small>}
      </div>
      {attribute?.type === "Colour" && (
       <div div className="mb-4">
        <input
         type="text"
         className="form-control form-control-sm"
         id="colour"
         name="colour"
         placeholder={`Enter ${location?.state?.key} code`}
         value={data.colour}
         onChange={handleChange}
        />
        {errors.colour && (
         <small className="text-danger">{errors.colour}</small>
        )}
       </div>
      )}

      {attribute?.type === "Label" && (
       <>
        <div div className="mb-4">
         <input
          type="text"
          className="form-control form-control-sm"
          id="colour"
          name="colour"
          placeholder={`Enter ${location?.state?.key} colour code`}
          value={data.colour}
          onChange={handleChange}
         />
         {errors.colour && (
          <small className="text-danger">{errors.colour}</small>
         )}
        </div>
        <div className="mb-4">
         <input
          type="number"
          className="form-control form-control-sm"
          id="value"
          name="value"
          placeholder={`Enter ${location?.state?.key} expiry days`}
          value={data.value}
          onChange={handleChange}
         />
        </div>
       </>
      )}

      <div className="col-md-12 mt-4 p-0" >
                    <SwitchToggle
                      checked={data.isActive}
                      onChange={(val) => setData((prev) => ({ ...prev, isActive: val }))}
                    />
                  </div>
     </ModalBody>
     <ModalFooter>
      <Button type="submit" color="primary" disabled={btnloading}>
       {btnloading ? (
        <>
         Loading <i className="fas fa-spinner fa-spin me-2" />
        </>
       ) : editingId ? (
        "Update"
       ) : (
        "Add"
       )}
      </Button>

      <Button color="danger" onClick={toggleModal}>
       Cancel
      </Button>
     </ModalFooter>
    </form>
   </Modal>
   <DeleteModel
    deleteModalOpen={deleteModalOpen}
    setDeleteModalOpen={() => setDeleteModalOpen(!deleteModalOpen)}
    onConfirm={confirmDelete}
    itemName="attribute value"
    isDeleting={isDeleting}
   />
  </>
 );
};

export default AttributeValue;
